import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useModalEscClose } from '@utils/hooks/use-modal-esc';
import { X, Loader2 } from 'lucide-react';
import { CreateAdminOrganizationPayload } from '@utils/types/admin';
import { useCreateAdminOrganization } from '@utils/api/admin/use-create-admin-organization';
import Input from '@components/atoms/ui-kit/input';
import toast from 'react-hot-toast';

type OrganizationFormModalProps = {
  organization: null; // Only used for creating
  onClose: () => void;
};

/**
 * Modal for creating a new organization.
 * Editing is done on the organization detail page.
 */
export default function OrganizationFormModal({
  onClose
}: OrganizationFormModalProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const createMutation = useCreateAdminOrganization();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [retentionDays, setRetentionDays] = useState<string>('90');
  const [maxUsers, setMaxUsers] = useState<string>('3');
  const [batchPriority, setBatchPriority] = useState<string>('200');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = t('name-is-required');
    if (!email.trim()) {
      newErrors.email = t('email-is-required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('invalid-email');
    }
    if (retentionDays && isNaN(Number(retentionDays)))
      newErrors.retentionDays = t('invalid-number');
    if (maxUsers && isNaN(Number(maxUsers)))
      newErrors.maxUsers = t('invalid-number');
    if (batchPriority && isNaN(Number(batchPriority)))
      newErrors.batchPriority = t('invalid-number');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload: CreateAdminOrganizationPayload = {
      name: name.trim(),
      email: email.trim(),
      is_active: isActive,
      retention_days: retentionDays ? Number(retentionDays) : undefined,
      max_users: maxUsers ? Number(maxUsers) : undefined,
      batch_priority: batchPriority ? Number(batchPriority) : undefined
    };

    createMutation.mutate(payload, {
      onSuccess: onClose,
      onError: (error: unknown) => {
        const err = error as {
          response?: {
            data?: {
              error_description?: string;
              detail?: string;
              message?: string;
            };
          };
        };
        const msg =
          err?.response?.data?.error_description ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          t('an-error-occurred');
        toast.error(msg);
      }
    });
  };

  useModalEscClose(true, onClose);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl"
        style={{ backgroundColor: getColour('baseLayer') }}
      >
        <div
          className="sticky top-0 flex items-center justify-between p-4 border-b z-10"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('baseLayer')
          }}
        >
          <h2
            className="text-lg font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {t('create-organization')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-2xl hover:bg-opacity-80"
            style={{ backgroundColor: getColour('thirdLayer') }}
          >
            <X className="h-5 w-5" style={{ color: getColour('normalText') }} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              id="org-name"
              label={t('name')}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('enter-organization-name')}
              error={errors.name}
              required
              size="small"
            />
            <Input
              id="org-email"
              label={t('email')}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('enter-email')}
              error={errors.email}
              required
              size="small"
            />
          </div>

          <div
            className="rounded-2xl border p-4"
            style={{
              borderColor: getColour('border'),
              backgroundColor: getColour('thirdLayer')
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <label
                  className="text-sm font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('active')}
                </label>
                <p
                  className="text-xs"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('organization-active-description')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsActive(!isActive)}
                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                style={{
                  backgroundColor: isActive
                    ? getColour('activeText')
                    : getColour('border')
                }}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`}
                  style={{ backgroundColor: getColour('baseLayer') }}
                />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              id="retention-days"
              label={t('retention-days')}
              type="number"
              numeric
              value={retentionDays}
              onChange={e => setRetentionDays(e.target.value)}
              placeholder="90"
              error={errors.retentionDays}
              size="small"
            />
            <Input
              id="max-users"
              label={t('max-users')}
              type="number"
              numeric
              value={maxUsers}
              onChange={e => setMaxUsers(e.target.value)}
              placeholder="3"
              error={errors.maxUsers}
              size="small"
            />
            <Input
              id="batch-priority"
              label={t('batch-priority')}
              type="number"
              numeric
              value={batchPriority}
              onChange={e => setBatchPriority(e.target.value)}
              placeholder="200"
              error={errors.batchPriority}
              size="small"
            />
          </div>
        </div>

        <div
          className="sticky bottom-0 flex items-center justify-end gap-3 p-4 border-t"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('baseLayer')
          }}
        >
          <button
            onClick={onClose}
            disabled={createMutation.isLoading}
            className="px-4 py-2 rounded-2xl text-sm font-medium"
            style={{
              backgroundColor: getColour('thirdLayer'),
              color: getColour('normalText')
            }}
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isLoading}
            className="px-4 py-2 rounded-2xl text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            style={{
              backgroundColor: getColour('activeText'),
              color: getColour('baseLayer')
            }}
          >
            {createMutation.isLoading && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            {t('create-organization')}
          </button>
        </div>
      </div>
    </div>
  );
}
